import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { AuthService } from '../../core/services/auth.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { MatDialog } from '@angular/material/dialog';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { references } from '../../core/services/references';

@Component({
  selector: 'app-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    NgOptimizedImage,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private destroyRef = inject(DestroyRef);

  form!: FormGroup;
  hide: boolean = true;
  loading: boolean = false;
  currentYear = new Date().getFullYear();

  constructor(
    private fb: FormBuilder,
    public _authService: AuthService,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _dialogRef: MatDialog,
    private _storage: SessionStorageService,
  ) {}

  ngOnInit(): void {
    this.onBuildForm();
  }

  onBuildForm(): void {
    this.form = this.fb.group({
      mail: [null, [Validators.required]],
      password: [null, Validators.required],
    });
  }

  forgotPassword() {
    const dialogRef = this._dialogRef.open(ForgotPasswordComponent, {
      autoFocus: false,
      minWidth: '350px',
      panelClass: 'custom-dialog-container',
      disableClose: false,
      data: '',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((x) => {
        if (x) {
          console.log(x);
        }
      });
  }

  onSubmit(): void {
    this.loading = true;
    // const formResult = this.form.getRawValue();
    const formResult = this.form.value;
    const body: any = {
      username: formResult.mail,
      password: formResult.password,
    };

    this._authService
      .onRequestLogin(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          const decodedToken = this._storage.decodeToken(resp.access_token);
          const allowedRoles = references._USER_ROLES;
          const userRoles = decodedToken.role;

          const hasAllowedRole =
            (typeof userRoles === 'string' &&
              allowedRoles.includes(userRoles)) ||
            (Array.isArray(userRoles) &&
              userRoles.some((role) => allowedRoles.includes(role)));

          if (!hasAllowedRole) {
            this._snackBar.open('Usuário não autorizado.', 'fechar', {
              horizontalPosition: 'right',
              verticalPosition: 'top',
              duration: 5000,
            });
            return;
          }

          this._authService.token$.next(resp.access_token);
          this._storage.save(references._STORAGE_TOKEN, resp.access_token);

          const jwtInfo = this._storage.getInfoFromToken(); // Decode jwt info
          this._authService.user$.next(jwtInfo.name);

          this._storage.save(references._STORAGE_USER, jwtInfo.name);
          this._router.navigate(['/main']);
        },
        error: (e) => {
          this.loading = false;
          this._snackBar.open(
            'Problemas ao realizar o login. Verifique o Usuário e senha!',
            'fechar',
            {
              horizontalPosition: 'right',
              verticalPosition: 'top',
              duration: 5000,
            },
          );
        },
        complete: () => {
          this.loading = false;
        },
      });
  }
}
